import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  Users, 
  Trophy,
  ChevronRight,
  Trash2,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await axios.get(`${API}/seasons`);
      setSeasons(response.data);
    } catch (error) {
      toast.error("Failed to load seasons");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/seasons/${deleteId}`);
      setSeasons(seasons.filter(s => s.id !== deleteId));
      toast.success("Season deleted successfully");
    } catch (error) {
      toast.error("Failed to delete season");
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Dashboard
          </h1>
          <p className="text-slate-500 mt-2">Manage your hockey seasons and teams</p>
        </div>
        <Button 
          onClick={() => navigate("/create-season")}
          className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm font-medium px-4 py-2 rounded-md transition-all active:scale-95"
          data-testid="create-season-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Season
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Seasons</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'var(--font-display)' }}>
                  {seasons.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Trophy className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Season Parts</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'var(--font-display)' }}>
                  {seasons.reduce((acc, s) => acc + (s.season_parts?.length || 0), 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Teams</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'var(--font-display)' }}>
                  {new Set(seasons.map(s => s.team_name)).size}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seasons List */}
      <div>
        <h2 
          className="text-xl font-semibold tracking-normal uppercase text-slate-900 mb-4"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Your Seasons
        </h2>

        {seasons.length === 0 ? (
          <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No seasons yet</h3>
              <p className="text-slate-500 mb-6">Create your first season to get started</p>
              <Button 
                onClick={() => navigate("/create-season")}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="empty-create-season-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Season
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {seasons.map((season, index) => (
              <Card 
                key={season.id} 
                className="bg-white border border-slate-200 rounded-xl shadow-sm card-hover fade-in cursor-pointer group"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/season/${season.id}`)}
                data-testid={`season-card-${season.id}`}
              >
                <CardHeader className="flex flex-row items-start justify-between pb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
                      {season.club_name}
                    </p>
                    <CardTitle 
                      className="text-lg font-bold text-slate-900 truncate"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      {season.title}
                    </CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(season.id);
                    }}
                    data-testid={`delete-season-${season.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {season.team_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {season.season_parts?.length || 0} parts
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      Click to manage
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Season</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this season? This will also delete all players and matches associated with it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
